/**
 * JusticeAccessGate — hard account gate for the /justiceisurright tenant
 * workspace (owner directive, Miles 2026-07-15: the page "is only allowed
 * for fernandohidalgo@yahoo.com login account and no public facing either").
 *
 * Decision table — evaluated only AFTER Firebase auth has SETTLED. The first
 * onAuthStateChanged emission is the hydration gate (firebase v11:
 * auth.authStateReady() resolves at exactly that point) — the same
 * discipline useTenantChatConfig adopted in F2, so a hard reload never
 * flash-redirects the legitimate signed-in account:
 *
 *   - auth still hydrating     → the app's standard loading state
 *     (ResilientSuspenseFallback, the same staged "loading…" UI the
 *     route-level Suspense shows). Never a redirect.
 *   - signed out / anonymous   → <Navigate to="/auth"> carrying returnUrl,
 *     mirroring ProtectedRoute in src/App.tsx.
 *   - signed in, wrong account → <Navigate to="/" replace> — an
 *     authenticated user who is not the allowlisted account is sent home
 *     (mirrors the app's legacy-route Navigate-home pattern; there is no
 *     partial view of this workspace).
 *   - signed in, allowlisted   → render the page (inside ErrorBoundary,
 *     mirroring ProtectedRoute).
 *
 * Deliberately NO moonshot-mode bypass: isMoonshotMode() is switched on by a
 * public URL parameter, so honouring it here (the way ProtectedRoute does
 * for the generic auth wall) would reopen the page to anyone. The owner
 * directive is an account allowlist, not a login wall.
 *
 * The page keeps its own internal gate UI (src/pages/justiceisurright.tsx)
 * as belt and braces; this route-level gate is the enforcement point.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { GuardStagedLoading } from './routing/GuardLoading';
import { isJiuAuthorizedEmail } from '../config/justiceisurrightAccess';

interface GateAuthState {
  /** True once the first onAuthStateChanged emission has been observed. */
  settled: boolean;
  /** True when a non-anonymous Firebase user is signed in. */
  hasUser: boolean;
  /** The signed-in user's email (null when signed out or email-less). */
  email: string | null;
}

const JusticeAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [authState, setAuthState] = React.useState<GateAuthState>({
    settled: false,
    hasUser: false,
    email: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    (async () => {
      try {
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        const auth = getAuth();
        // The subscription stays live so a mid-session sign-out or account
        // switch re-evaluates the gate; the FIRST emission doubles as the
        // hydration gate (no decision is made before it).
        unsubscribe = onAuthStateChanged(auth, (user) => {
          if (cancelled) return;
          const isRealUser = !!user && !user.isAnonymous;
          setAuthState({
            settled: true,
            hasUser: isRealUser,
            email: isRealUser ? user.email || null : null,
          });
        });
        if (cancelled && unsubscribe) {
          try { unsubscribe(); } catch { /* noop */ }
        }
      } catch {
        // Firebase unavailable — fail CLOSED: treat as signed out.
        if (!cancelled) setAuthState({ settled: true, hasUser: false, email: null });
      }
    })();
    return () => {
      cancelled = true;
      if (unsubscribe) {
        try { unsubscribe(); } catch { /* noop */ }
      }
    };
  }, []);

  // Never decide before auth has settled — no flash-redirect during
  // hydration; show the app's standard loading state instead, in the light
  // of the page it guards (D-92).
  if (!authState.settled) {
    return <GuardStagedLoading ahead={children} gateName="justiceisurright-account-gate" />;
  }

  // Signed out (or anonymous): to /auth with returnUrl, per ProtectedRoute.
  if (!authState.hasUser) {
    return (
      <Navigate
        to="/auth"
        state={{ returnUrl: location.pathname + location.search }}
      />
    );
  }

  // Signed in but not the allowlisted account: home.
  if (!isJiuAuthorizedEmail(authState.email)) {
    return <Navigate to="/" replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

export default JusticeAccessGate;
